// WbTicketCentrePaneWeight — Phase C10 Group 3.
//
// Decides what to render based on the ticket's weighing_mode and which
// weights already exist. The orchestration is:
//
//   two_pass    — capture gross first, then tare. Renders an informational
//                 "vehicle has left" card once a gross is captured (tare
//                 still pending) AND keeps a capture widget visible so the
//                 operator can take the tare from this screen on return.
//   stored_tare — capture gross only. The server populates tare from the
//                 vehicle's stored value once gross lands.
//   manual_tare — capture gross, then expose a separate manual-tare input
//                 + "Set tare" button (calls captureWeight with method=
//                 'manual').
//   units_only  — no weighing; just an informational card.
//
// We pull the live readout / SSE wiring out into WbWeightDisplay so the
// gross / tare widgets stay identical in shape.

import { useState } from 'react';
import { Card } from '../../../../components/ui/Card/Card';
import { Button } from '../../../../components/ui/Button/Button';
import { Badge } from '../../../../components/ui/Badge/Badge';
import { useToastStore } from '../../../../store/toastStore';
import { extractErrorMessage } from '../../../../utils/errorMessage';
import { useWbTicket } from '../../context/WbTicketContext';
import { useWbLiveWeight } from '../../context/WbLiveWeightContext';
import { useWbWeighbridges } from '../../hooks/useWbWeighbridges';
import { useWbLiveBridges } from '../../../../hooks/wb/useWbLiveBridges';
import { useActiveBridge } from '../../../../hooks/wb/useActiveBridge';
import { BridgeSelectorRow } from '../../../../components/wb/BridgeSelectorRow';
import { WbWeightDisplay } from './WbWeightDisplay';
import styles from './WbTicketCentrePane.module.css';

const KG_FORMATTER = new Intl.NumberFormat('en-GB', {
  maximumFractionDigits: 1,
});

function fmtKg(n: number | null): string {
  if (n === null || n === undefined) return '—';
  return `${KG_FORMATTER.format(n)} kg`;
}

export function WbTicketCentrePaneWeight(): JSX.Element {
  const {
    ticket,
    captureWeight,
    isCapturingWeight,
    activeLineUid,
    activeLineWeights,
    captureActiveLine,
    hasWeightLines,
    setTareAutoFilled,
  } = useWbTicket();
  const { liveWeightKg, liveWeightStable, setLiveWeight } = useWbLiveWeight();
  const pushToast = useToastStore((s) => s.push);

  // Tickets aren't pinned to a specific weighbridge, so `ticket.weighbridge` is
  // usually null and the live-weight SSE gate (weighbridgeEncId !== null) would
  // never open. Fall back to the first active weighbridge from the cached list
  // — its enc_id is stable across renders (React Query) and a single-bridge
  // company (the common case) has exactly one to pick. The enc_id must come
  // from this list, never a PATCH echo, so it stays decryptable server-side.
  const { data: weighbridgesData } = useWbWeighbridges({ is_active: true });
  const fallbackWeighbridgeEncId =
    (weighbridgesData?.weighbridges ?? [])[0]?.enc_id ?? null;

  const [manualTare, setManualTare] = useState<string>('');

  const mockAgentMode =
    (import.meta.env.VITE_WB_MOCK_AGENT_MODE as string | undefined) === 'true';
  const ticketActive =
    ticket !== null &&
    ticket.status !== 'completed' &&
    ticket.status !== 'voided';

  // Multi-bridge selector. The BridgeSelectorRow owns the single live-weight
  // subscription for this surface and feeds the ACTIVE bridge's reading into
  // the shared context (setLiveWeight); the legacy WbWeightDisplay therefore
  // runs with feedEnabled={false} (it reads that context). Capture targets the
  // active bridge's enc_id. With no live bridges (endpoint empty / mock mode)
  // the row hides and the legacy single-bridge fallback applies unchanged.
  const { data: liveBridgesData } = useWbLiveBridges({ enabled: !mockAgentMode });
  const bridges = liveBridgesData?.bridges ?? [];
  const { activeBridge, activeBridgeId, setActiveBridge } = useActiveBridge(bridges);
  const showBridgeRow = !mockAgentMode && bridges.length > 0;
  // Capture is blocked when bridges exist but none is active / the active one
  // is disconnected — the operator must explicitly pick a connected bridge.
  const captureBridgeBlocked =
    showBridgeRow && (activeBridge === null || !activeBridge.agent_online);

  // -- No ticket yet ----------------------------------------------------
  //
  // The orchestrator's centre pane still wants something to render so the
  // operator sees where the weight capture WILL appear. We hand
  // WbWeightDisplay a disabled flag — the readout is muted and the
  // Capture button is locked.
  if (ticket === null) {
    return (
      <Card title="Weight">
        <span className={styles.helperMuted}>
          Pick a customer first to enable weight capture.
        </span>
      </Card>
    );
  }

  // The active bridge (multi-bridge row) wins; falls back to the ticket's
  // pinned bridge, then the first active weighbridge (legacy single-bridge).
  const weighbridgeEncId =
    activeBridge?.enc_id ?? ticket.weighbridge?.enc_id ?? fallbackWeighbridgeEncId;

  async function handleGrossCapture(
    kg: number,
    isStable: boolean,
    method: 'hardware' | 'manual',
  ) {
    try {
      await captureWeight({
        weight_type: 'gross',
        weight_kg: kg,
        method,
        is_stable: isStable,
        ...(weighbridgeEncId !== null
          ? { weighbridge_enc_id: weighbridgeEncId }
          : {}),
      });
    } catch (err) {
      pushToast({
        type: 'error',
        message: extractErrorMessage(err) ?? 'Failed to capture gross.',
      });
    }
  }

  async function handleTareCapture(
    kg: number,
    isStable: boolean,
    method: 'hardware' | 'manual',
  ) {
    try {
      await captureWeight({
        weight_type: 'tare',
        weight_kg: kg,
        method,
        is_stable: isStable,
        ...(weighbridgeEncId !== null
          ? { weighbridge_enc_id: weighbridgeEncId }
          : {}),
      });
    } catch (err) {
      pushToast({
        type: 'error',
        message: extractErrorMessage(err) ?? 'Failed to capture tare.',
      });
    }
  }

  async function handleManualTareClick() {
    const parsed = Number(manualTare);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    try {
      await captureWeight({
        weight_type: 'tare',
        weight_kg: parsed,
        method: 'manual',
        is_stable: true,
        ...(weighbridgeEncId !== null
          ? { weighbridge_enc_id: weighbridgeEncId }
          : {}),
      });
      setManualTare('');
    } catch (err) {
      pushToast({
        type: 'error',
        message: extractErrorMessage(err) ?? 'Failed to set tare.',
      });
    }
  }

  const ticketLocked =
    ticket.status === 'completed' || ticket.status === 'voided';

  // -- Per-line capture (active line + auto-advance top button) ---------
  //
  // The top "Capture Gross/Tare" button targets the line the operator has
  // selected in the editor. What it captures next is derived from that line's
  // weights: gross until one exists, then tare; once both are set there's
  // nothing left to capture (the editor auto-advances past it). The live
  // reading must be STABLE before the top button enables — unlike the per-row
  // buttons, this one waits for a settled weight before committing it.
  const activeGross = activeLineWeights?.gross_weight_kg ?? null;
  const activeTare = activeLineWeights?.tare_weight_kg ?? null;
  const nextCaptureType: 'gross' | 'tare' | null =
    activeLineUid === null
      ? null
      : !activeGross
        ? 'gross'
        : !activeTare
          ? 'tare'
          : null;
  const liveReady = liveWeightKg !== null && liveWeightStable;
  const topCaptureDisabled =
    activeLineUid === null ||
    nextCaptureType === null ||
    !liveReady ||
    ticketLocked ||
    captureBridgeBlocked;
  const topCaptureLabel = captureBridgeBlocked
    ? 'Select a bridge'
    : activeLineUid === null
      ? 'Select a line to capture'
      : nextCaptureType === 'gross'
        ? 'Capture Gross'
        : nextCaptureType === 'tare'
          ? 'Capture Tare'
          : 'Line fully captured';

  function handleTopCapture(): void {
    if (activeLineUid === null || nextCaptureType === null) return;
    // The button only enables on a stable reading, so the current value IS the
    // stable weight — no separate wait-for-stable subscription needed.
    if (liveWeightKg === null || !liveWeightStable) return;
    captureActiveLine(nextCaptureType, liveWeightKg);
  }

  // The per-line capture card is the always-visible weight UI whenever a
  // weight-mode line exists: it shows the live scale reading whether or not a
  // line is selected, and only disappears when the ticket is locked (completed
  // / voided → read-only summary below), the mode needs no weighing
  // (units_only), or there are no weight lines yet (nothing to capture into —
  // the legacy ticket-level widget covers that case). It must NOT hide just
  // because no line is selected — the operator sees the live reading at all
  // times.
  const perLineCapture =
    ticketLocked || ticket.weighing_mode === 'units_only' || !hasWeightLines ? null : (
      <Card title="Per-line capture">
        {/* Live reading — always visible, independent of line selection. */}
        <div className={styles.weightWidget} data-testid="wb-perline-live">
          <span className={styles.weightLabel}>Live weight</span>
          <div className={styles.weightReadoutRow}>
            <span
              className={`${styles.weightDot} ${
                liveWeightStable
                  ? styles.weightDotStable
                  : styles.weightDotUnstable
              }`}
              aria-hidden
            />
            <span className={styles.weightReadout}>
              {liveWeightKg === null
                ? '—'
                : KG_FORMATTER.format(liveWeightKg)}
              <span className={styles.weightUnit}>kg</span>
            </span>
          </div>
          <span className={styles.weightStreamHint}>
            {liveWeightKg === null
              ? 'Awaiting reading…'
              : liveWeightStable
                ? 'Stable — ready to capture.'
                : 'Awaiting stable reading…'}
          </span>
        </div>
        {activeLineUid !== null ? (
          <div
            className={styles.weightSummary}
            data-testid="wb-active-line-readout"
          >
            <span className={styles.weightSummaryLabel}>Gross</span>
            <span />
            <span className={styles.weightSummaryValue}>
              {fmtKg(activeGross)}
            </span>
            <span className={styles.weightSummaryLabel}>Tare</span>
            <span />
            <span className={styles.weightSummaryValue}>
              {fmtKg(activeTare)}
            </span>
            <span className={styles.weightSummaryLabel}>Net</span>
            <span />
            <span className={styles.weightSummaryValue}>
              {fmtKg(activeLineWeights?.net_weight_kg ?? null)}
            </span>
          </div>
        ) : (
          <span className={styles.helperMuted}>
            Select a line below to capture its weight.
          </span>
        )}
        <Button
          variant="primary"
          onClick={handleTopCapture}
          disabled={topCaptureDisabled}
          data-testid="wb-active-line-capture"
          style={{ marginTop: '0.5rem' }}
        >
          {topCaptureLabel}
        </Button>
        {activeLineUid !== null && nextCaptureType !== null && !liveReady ? (
          <span
            className={styles.helperMuted}
            data-testid="wb-active-line-capture-hint"
          >
            Awaiting a stable reading…
          </span>
        ) : null}
      </Card>
    );

  const modeBody = ((): JSX.Element => {
  // Locked tickets are read-only — show the captured summary, never a capture
  // widget (covers Fix-1 "completed/voided → read-only").
  if (ticketLocked && ticket.weighing_mode !== 'units_only') {
    return <WeightSummaryCard ticket={ticket} />;
  }
  // When the per-line capture card is on screen (weight lines present) it owns
  // weighing; rendering the legacy ticket-level widget here would only
  // duplicate the live readout + capture button.
  if (hasWeightLines && ticket.weighing_mode !== 'units_only') {
    return <></>;
  }
  // -- weighing_mode = units_only --------------------------------------
  if (ticket.weighing_mode === 'units_only') {
    return (
      <Card title="Weight">
        <div className={styles.infoCard}>
          No weighing required for this ticket. Add line items below.
        </div>
      </Card>
    );
  }

  // -- weighing_mode = two_pass ----------------------------------------
  if (ticket.weighing_mode === 'two_pass') {
    // 1) No gross yet → capture gross.
    if (ticket.gross_weight_kg === null) {
      return (
        <Card title="Weight">
          <WbWeightDisplay
            weighbridgeEncId={weighbridgeEncId}
            feedEnabled={!showBridgeRow}
            disabled={captureBridgeBlocked || isCapturingWeight}
            weightType="gross"
            onCapture={handleGrossCapture}
          />
        </Card>
      );
    }
    // 2) Gross captured, no tare → capture tare. For two-pass tickets the
    //    vehicle leaves between weighs, so render an informational banner
    //    above so the operator understands the in-between flow.
    if (ticket.tare_weight_kg === null) {
      return (
        <Card title="Weight">
          <div
            className={`${styles.infoCard} ${styles.infoCardEmphasis}`}
            data-testid="wb-awaiting-second-weigh-banner"
          >
            Vehicle has left — awaiting return for tare. Capture tare
            below when the vehicle is back on the bridge.
          </div>
          <div className={styles.weightSummary}>
            <span className={styles.weightSummaryLabel}>Gross captured</span>
            <span />
            <span className={styles.weightSummaryValue}>
              {fmtKg(ticket.gross_weight_kg)}
            </span>
          </div>
          <WbWeightDisplay
            weighbridgeEncId={weighbridgeEncId}
            feedEnabled={!showBridgeRow}
            disabled={captureBridgeBlocked || isCapturingWeight}
            weightType="tare"
            onCapture={handleTareCapture}
          />
        </Card>
      );
    }
    // 3) Both captured → summary.
    return <WeightSummaryCard ticket={ticket} />;
  }

  // -- weighing_mode = stored_tare -------------------------------------
  if (ticket.weighing_mode === 'stored_tare') {
    if (ticket.gross_weight_kg === null) {
      return (
        <Card title="Weight">
          <WbWeightDisplay
            weighbridgeEncId={weighbridgeEncId}
            feedEnabled={!showBridgeRow}
            disabled={captureBridgeBlocked || isCapturingWeight}
            weightType="gross"
            onCapture={handleGrossCapture}
          />
          <div className={styles.helperMuted} style={{ marginTop: '0.5rem' }}>
            Stored tare will be used automatically once gross is captured.
          </div>
        </Card>
      );
    }
    return <WeightSummaryCard ticket={ticket} />;
  }

  // -- weighing_mode = manual_tare -------------------------------------
  if (ticket.weighing_mode === 'manual_tare') {
    if (ticket.gross_weight_kg === null) {
      return (
        <Card title="Weight">
          <WbWeightDisplay
            weighbridgeEncId={weighbridgeEncId}
            feedEnabled={!showBridgeRow}
            disabled={captureBridgeBlocked || isCapturingWeight}
            weightType="gross"
            onCapture={handleGrossCapture}
          />
        </Card>
      );
    }
    if (ticket.tare_weight_kg === null) {
      const manualValid =
        Number.isFinite(Number(manualTare)) && Number(manualTare) > 0;
      return (
        <Card title="Weight">
          <div className={styles.weightSummary}>
            <span className={styles.weightSummaryLabel}>Gross captured</span>
            <span />
            <span className={styles.weightSummaryValue}>
              {fmtKg(ticket.gross_weight_kg)}
            </span>
          </div>
          <div className={styles.weightWidget}>
            <span className={styles.weightLabel}>Tare weight (manual)</span>
            <div className={styles.manualWeightRow}>
              <input
                type="number"
                className={styles.manualWeightInput}
                value={manualTare}
                onChange={(e) => {
                  setManualTare(e.target.value);
                  setTareAutoFilled(false);
                }}
                step={0.1}
                min={0.1}
                placeholder="0.0"
                aria-label="Tare weight value in kilograms"
                data-testid="wb-manual-tare-input"
              />
              <span className={styles.weightUnit}>kg</span>
            </div>
            <Button
              variant="primary"
              onClick={() => {
                void handleManualTareClick();
              }}
              disabled={!manualValid || isCapturingWeight}
              data-testid="wb-manual-tare-set"
            >
              Set tare
            </Button>
          </div>
        </Card>
      );
    }
    return <WeightSummaryCard ticket={ticket} />;
  }

  // Defensive — unknown mode. Shouldn't happen if the server validates
  // weighing_mode on create.
  return (
    <Card title="Weight">
      <span className={styles.helperMuted}>
        Unknown weighing mode: {String(ticket.weighing_mode)}
      </span>
    </Card>
  );
  })();

  return (
    <>
      {showBridgeRow ? (
        <BridgeSelectorRow
          bridges={bridges}
          activeBridgeId={activeBridgeId}
          onSelect={setActiveBridge}
          onActiveReading={(value, stable) => setLiveWeight(value, stable)}
          variant="main"
          feedEnabled={ticketActive}
        />
      ) : null}
      {perLineCapture}
      {modeBody}
    </>
  );
}

// -- Weight summary card ----------------------------------------------------

interface WeightSummaryCardProps {
  ticket: {
    gross_weight_kg: number | null;
    tare_weight_kg: number | null;
    net_weight_kg: number | null;
    gross_method: 'hardware' | 'manual' | null;
    tare_method: 'hardware' | 'manual' | 'stored' | null;
  };
}

function WeightSummaryCard({ ticket }: WeightSummaryCardProps): JSX.Element {
  const net =
    ticket.net_weight_kg ??
    (ticket.gross_weight_kg !== null && ticket.tare_weight_kg !== null
      ? ticket.gross_weight_kg - ticket.tare_weight_kg
      : null);

  return (
    <Card title="Weight">
      <div className={styles.weightSummary} data-testid="wb-weight-summary">
        <span className={styles.weightSummaryLabel}>Gross</span>
        <span>
          {ticket.gross_method !== null ? (
            <Badge variant="muted">{ticket.gross_method}</Badge>
          ) : null}
        </span>
        <span className={styles.weightSummaryValue}>
          {fmtKg(ticket.gross_weight_kg)}
        </span>

        <span className={styles.weightSummaryLabel}>Tare</span>
        <span>
          {ticket.tare_method !== null ? (
            <Badge variant="muted">{ticket.tare_method}</Badge>
          ) : null}
        </span>
        <span className={styles.weightSummaryValue}>
          {fmtKg(ticket.tare_weight_kg)}
        </span>

        <span className={styles.weightSummaryLabel}>Net</span>
        <span />
        <span className={styles.weightSummaryValue}>{fmtKg(net)}</span>
      </div>
    </Card>
  );
}
